import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import { useEffect } from "react";

import { LearnCardControlsPresentationContext } from "#learn-card-controls-presentation-context";
import { LearnDeckContext } from "#learn-deck-context";
import type { LearnCardControls } from "#learn-deck-context-value";

/**
 * Lend the deck's action area this card's controls for as long as this card is the current one.
 *
 * The ONLY supported way to register. A card must never call `registerCardControls` itself: the
 * claim has to carry the presentation that owns it, and that owner comes from the nearest card,
 * never from deck state (which has already advanced to the successor by the time a leaving card
 * runs its cleanup). Registering unkeyed is what previously let a card being dismissed erase the
 * controls of the card replacing it, leaving a fully rendered card whose Reveal and rating
 * buttons did nothing until the deck was remounted.
 *
 * `controls` is expected to be recreated per render by the caller; the effect re-registers on
 * every change, which is the same claim from the same presentation and so always wins.
 */
export function useRegisterLearnCardControls(controls: LearnCardControls): void {
  const presentation = useContextSelectors(LearnCardControlsPresentationContext, (c) => c)[0];
  const [registerCardControls] = useContextSelectors(
    LearnDeckContext,
    (c) => c.registerCardControls,
  );
  const presentationKey = learnCardPresentationKey(presentation);
  useEffect(() => {
    registerCardControls(presentationKey, controls);
    return () => {
      registerCardControls(presentationKey, null);
    };
  }, [controls, presentationKey, registerCardControls]);
}
